<template>
  <PageContainer class="PageContainer" v-if="data" :breadcrumbs="breadcrumbs">
    <h1 class="pageHeader">{{ data.pack.packName }}</h1>

    <div v-if="prevPackLink || nextPackLink" class="otherPackLinks">
      <div class="PrevLink">
        <template v-if="prevPackLink">
          <Caret class="PrevIcon" />
          <TextLink
            :to="prevPackLink"
            size="sm"
            text="1つ前のパックを見る"
          />
        </template>
      </div>

      <div class="NextLink">
        <template v-if="nextPackLink">
          <TextLink
            :to="nextPackLink"
            size="sm"
            text="1つ後のパックを見る"
          />
          <Caret class="NextIcon" />
        </template>
      </div>
    </div>

    <div class="Body" v-if="data.pokemonSingleProducts.length">
      <div class="SummaryWrapper" v-for="product in data.pokemonSingleProducts" :key="product.inventory_product_id" >
        <Summary class="Summary" :data="product" :disable-view-transition />
      </div>
    </div>

    <div v-if="(prevPackLink || nextPackLink) && !!data.pokemonSingleProducts.length" class="otherPackLinks">
      <div class="PrevLink">
        <template v-if="prevPackLink">
          <Caret class="PrevIcon" />
          <TextLink
            :to="prevPackLink"
            size="sm"
            text="1つ前のパックを見る"
          />
        </template>
      </div>

      <div class="NextLink">
        <template v-if="nextPackLink">
          <TextLink
            :to="nextPackLink"
            size="sm"
            text="1つ後のパックを見る"
          />
          <Caret class="NextIcon" />
        </template>
      </div>
    </div>
  </PageContainer>
</template>

<script lang="ts">
import { definePageMeta, useSeoMeta, useHead, useRuntimeConfig } from '#imports'
import {
  defineNuxtComponent,
  useAsyncData,
  useNuxtApp,
  createError,
  useRouter,
} from "#app";
import { storeToRefs } from 'pinia'
import { DITokens } from "@/di";
import { computed } from 'vue'
import type { ResponseType as PackResponseType } from "@/server/api/pokemon/packs/[packId]/index.get";
import type { ResponseType as GetPacksResponseType } from "@/server/api/pokemon/packs/index.get";
import Summary from '@/components/Pokemon/Single/Summary'
import PageContainer from '@/containers/Page'
import { PokemonSeries } from '@/const/pokemon'
// @ts-expect-error
import { Caret } from '@ka-nabellinc/ka-nabell-icons'
import { TextLink } from '@ka-nabellinc/ka-nabell-ui'

export default defineNuxtComponent({
  components: {
    Summary,
    PageContainer,
    TextLink,
    Caret,
  },
  async setup() {
    const { public: { APP_ENV } } = useRuntimeConfig()

    definePageMeta({
      genre: 'pokemon',
      footer: {
        isShowSocialMedia: true,
        isShowShopSeries: true,
      }
    })

    const route = useRouter().currentRoute.value
    const { packId } = route.params;
    const { $container } = useNuxtApp();
    const cartStore = $container.resolve(DITokens.CartStore);
    const cartState = storeToRefs(cartStore);
    const disableViewTransition = computed(() => cartState.showCart.value) // カート表示中はview transitionをoff
    const pokemonStore = $container.resolve(DITokens.PokemonStore)
    const {
      packs,
      sortedPacksForPackPage,
    } = storeToRefs(pokemonStore)

    const { data, error } = await useAsyncData(
      `pokemonPackPagedata_${packId}`,
      async () => {
        const [{ pack }, pokemonSingleProducts] = await Promise.all([
          $fetch<PackResponseType>(`/api/pokemon/packs/${packId}`),
          $container
            .resolve(DITokens.ElasticsearchGateway)
            .getPokemonSingleProductsByPackId(packId as string),
        ]);

        if (packs.value.length === 0) {
          const res = await $fetch<GetPacksResponseType>("/api/pokemon/packs");
          packs.value = res.packs;
        }

        return {
          pack,
          pokemonSingleProducts,
        };
      }
    );

    if (error.value || !data.value) {
      throw createError({
        statusCode: 404,
        statusMessage: "Page not found",
      });
    }

    const prevPackLink = computed(() => {
      const currentPackIndex = sortedPacksForPackPage.value.findIndex(p => p.packId === data.value?.pack.packId)
      if (currentPackIndex < 0) {
        return null
      }

      const prevPackId = sortedPacksForPackPage.value[currentPackIndex + 1]?.packId
      if (typeof prevPackId !== 'number') {
        return null
      }

      return `/pokemon/series/${data.value?.pack.series?.seriesId}/packs/${prevPackId}`
    })

    const nextPackLink = computed(() => {
      const currentPackIndex = sortedPacksForPackPage.value.findIndex(p => p.packId === data.value?.pack.packId)
      if (currentPackIndex < 0) {
        return null
      }
      
      const nextPackId = sortedPacksForPackPage.value[currentPackIndex - 1]?.packId
      if (typeof nextPackId !== 'number') {
        return null
      }

      return `/pokemon/series/${data.value?.pack.series?.seriesId}/packs/${nextPackId}`
    })

    // Set Breadcrumbs
    const seriesId = data.value.pack.series?.seriesId
    const seriesNameStr = typeof seriesId === 'number' ? PokemonSeries[seriesId] : 'シリーズ'
    const seriesPageLink = typeof seriesId === 'number' ? `/pokemon/series/${seriesId}` : undefined
    const packNameStr = data.value.pack.packName

    const title = packNameStr + ' - カーナベル'
    const description = `${packNameStr}のカードを状態別に納得価格で販売中！最速で翌日午前に入手可能！`

    useSeoMeta({
      title,
      description,
      ogTitle: title,
      ogDescription: description,
      robots: APP_ENV === 'production' ? 'index,follow' : 'noindex,nofollow',
      twitterSite: '@ka_nabell_pcg',
      ogSiteName: 'ポケカ販売ならカーナベル！',
      ogUrl: `https://sell.ka-nabell.com/pokemon/series/${seriesId}/packs/${packId}`
    });

    useHead({
      link: [
        {
          rel: 'canonical',
          href: `https://sell.ka-nabell.com/pokemon/series/${seriesId}/packs/${packId}`
        }
      ]
    })

    const breadcrumbs = [
      {
        title: 'トレカ販売トップ',
        link: '/',
      },
      {
        title: 'ポケカ販売トップ',
        link: '/pokemon',
      },
      {
        title: seriesNameStr,
        link: seriesPageLink,
      },
      {
        title: packNameStr,
      },
    ]

    return {
      data,
      breadcrumbs,
      disableViewTransition,
      prevPackLink,
      nextPackLink,
    };
  },
});
</script>

<style scoped>
.Body {
  width: 100%;
  display: flex;
  column-gap: 16px;
  row-gap: 20px;
  flex-wrap: wrap;
}

.SummaryWrapper {
  width: calc((100% - (16px * 2)) / 3);
}

.pageHeader {
  width: 100%;
}

.PageContainer {
  gap: 24px;
}

.otherPackLinks {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.PrevLink,
.NextLink {
  display: flex;
  align-items: center;
  gap: 4px;
}

.PrevIcon {
  width: 20px;
  height: 20px;
  padding-left: 2px;
  transform: rotate(90deg);
}

.NextIcon {
  width: 20px;
  height: 20px;
  padding-right: 2px;
  transform: rotate(-90deg);
}
</style>
